import React, { useState } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  CalendarIcon, Loader2, DollarSign, CreditCard, 
  Trash2, Edit, Lock, Unlock, Share2, Repeat, 
  ShieldAlert, Copy, CheckCircle, ArrowDownToLine,
  Clock, AlertTriangle, Crown
} from 'lucide-react';
import { cn } from '../lib/utils';
import { VaultRefundTracker } from '../components/RefundTracker';
import { TemplatePicker } from '../components/TemplatePicker';
import { VaultSocialPanel } from '../components/VaultSocialPanel';

const API = process.env.REACT_APP_BACKEND_URL;

// ===================== CREATE VAULT =====================
export function CreateVaultModal({ open, onClose, onSuccess }) {
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [targetAmount, setTargetAmount] = useState('');
  const [unlockDate, setUnlockDate] = useState(null);
  const [vaultType, setVaultType] = useState('basic');
  const [penaltyPercent, setPenaltyPercent] = useState('10');
  const [cooldownHours, setCooldownHours] = useState('72');
  const [loading, setLoading] = useState(false);
  const [templateId, setTemplateId] = useState(null);

  const handleSubmit = async () => {
    if (!name || !targetAmount || !unlockDate) {
      toast.error('Fill in all fields to create your vault');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/api/goals`, {
        name,
        target_amount: parseFloat(targetAmount),
        unlock_date: unlockDate.toISOString(),
        description: '',
        vault_type: vaultType,
        penalty_percent: vaultType === 'commitment' ? parseFloat(penaltyPercent) : 0,
        cooldown_hours: vaultType !== 'basic' ? parseInt(cooldownHours) : 0
      }, { withCredentials: true });
      onSuccess();
      handleClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1); setName(''); setTargetAmount(''); setUnlockDate(null); setVaultType('basic'); setTemplateId(null);
    onClose();
  };

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const totalSteps = vaultType === 'basic' ? 3 : 4;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="glass-modal sm:max-w-md" data-testid="create-goal-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-white">
            {step === 1 ? 'Name your vault' : step === 2 ? 'Set your amount' : step === 3 ? 'Pick a lock date' : 'Choose vault strength'}
          </DialogTitle>
          <DialogDescription className="text-[#A19DB0]">
            {step === 1 ? 'Give it a name so you remember what you\'re saving for.' : 
             step === 2 ? 'How much are you saving toward?' : 
             step === 3 ? 'Your money will be locked until this date.' :
             'How tough do you want your vault to be?'}
          </DialogDescription>
        </DialogHeader>

        {/* Progress dots */}
        <div className="flex justify-center gap-2 mt-2">
          {Array.from({length: totalSteps}, (_, i) => i + 1).map(s => (
            <div key={s} className={`h-2 rounded-full transition-all ${s === step ? 'w-8 bg-[#9333EA]' : s < step ? 'w-2 bg-[#9333EA]' : 'w-2 bg-[#12121A]'}`} />
          ))}
        </div>

        <div className="mt-6">
          {step === 1 && (
            <div className="space-y-4">
              <TemplatePicker selectedId={templateId} onPick={(t) => {
                setTemplateId(t?.template_id || null);
                if (!t) { setName(''); setTargetAmount(''); setUnlockDate(null); setVaultType('basic'); return; }
                const d = new Date(); d.setDate(d.getDate() + t.duration_days);
                setName(t.name); setTargetAmount(String(t.target_amount)); setUnlockDate(d); setVaultType(t.vault_type || 'basic');
              }} />
              <Input data-testid="goal-name-input" value={name} onChange={(e) => setName(e.target.value)} className="input-dark h-14 text-lg" placeholder="e.g. New Car, Vacation, Rent" autoFocus />
              <p className="text-xs text-[#A19DB0]">This helps you stay organized when you have multiple vaults.</p>
              <Button data-testid="create-step1-next-btn" onClick={() => { if (name.trim()) setStep(2); else toast.error('Give your vault a name'); }} className="w-full h-12 btn-primary font-semibold">Next</Button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div className="relative">
                <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#A19DB0]" />
                <Input data-testid="target-amount-input" type="number" min="1" step="0.01" value={targetAmount} onChange={(e) => setTargetAmount(e.target.value)} className="pl-12 input-dark h-14 text-lg font-mono" placeholder="0.00" autoFocus />
              </div>
              <p className="text-xs text-[#A19DB0]">You can deposit any amount toward this target over time.</p>
              <div className="flex gap-3">
                <Button data-testid="create-step2-back-btn" onClick={() => setStep(1)} variant="outline" className="flex-1 btn-secondary">Back</Button>
                <Button data-testid="create-step2-next-btn" onClick={() => { if (targetAmount && parseFloat(targetAmount) > 0) setStep(3); else toast.error('Enter a target amount'); }} className="flex-1 h-12 btn-primary font-semibold">Next</Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-[#A19DB0] mb-2 block">Lock until this date</Label>
                <Input
                  data-testid="unlock-date-input"
                  type="date"
                  min={(() => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().split('T')[0]; })()}
                  value={unlockDate ? unlockDate.toISOString().split('T')[0] : ''}
                  onChange={(e) => {
                    if (e.target.value) {
                      const [y, m, d] = e.target.value.split('-').map(Number);
                      setUnlockDate(new Date(y, m - 1, d, 12, 0, 0));
                    } else {
                      setUnlockDate(null);
                    }
                  }}
                  className="input-dark h-14 text-lg"
                />
                <p className="text-xs text-[#A19DB0] mt-2">Tap the date field to open the calendar on your phone.</p>
              </div>
              {unlockDate && unlockDate.getFullYear() > 2000 && (
                <div className="p-4 bg-[#9333EA]/10 border border-[#9333EA]/20 rounded-xl">
                  <div className="flex items-center gap-2 mb-1">
                    <Lock className="h-4 w-4 text-[#9333EA]" />
                    <span className="text-sm font-semibold text-white">Your money will be locked</span>
                  </div>
                  <p className="text-sm text-[#A19DB0]">
                    until <span className="text-white font-semibold">{format(unlockDate, "MMMM d, yyyy")}</span>. You won't be able to withdraw before this date.
                  </p>
                </div>
              )}
              <div className="flex gap-3">
                <Button data-testid="create-step3-back-btn" onClick={() => setStep(2)} variant="outline" className="flex-1 btn-secondary">Back</Button>
                {vaultType !== 'basic' ? (
                  <Button data-testid="create-step3-next-btn" onClick={() => { if (unlockDate) setStep(4); else toast.error('Pick a date'); }} className="flex-1 h-12 btn-primary font-semibold">Next</Button>
                ) : (
                  <Button data-testid="submit-goal-button" onClick={handleSubmit} disabled={loading || !unlockDate} className="flex-1 h-12 btn-primary font-semibold">
                    {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Lock It In'}
                  </Button>
                )}
              </div>
              {/* Vault type selector */}
              <div className="pt-3 border-t border-white/10">
                <p className="text-xs font-semibold uppercase tracking-wider text-[#A19DB0] mb-3">Vault Strength</p>
                <div className="space-y-2">
                  <VaultTypeOption type="basic" selected={vaultType} onSelect={setVaultType} label="Basic Vault" desc="Locked until set date. Withdraw when it unlocks." free />
                  <VaultTypeOption type="commitment" selected={vaultType} onSelect={setVaultType} label="Commitment Vault" desc="Withdraw early = lose 5-20% penalty. Real consequences." premium />
                  <VaultTypeOption type="no_escape" selected={vaultType} onSelect={setVaultType} label="No Escape Vault" desc="Zero early withdrawal. Locked until the date. Period." premium />
                </div>
              </div>
            </div>
          )}

          {step === 4 && vaultType !== 'basic' && (
            <div className="space-y-4">
              {vaultType === 'commitment' && (
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-[#A19DB0] mb-2 block">Penalty if you withdraw early</Label>
                    <Select value={penaltyPercent} onValueChange={setPenaltyPercent}>
                      <SelectTrigger className="input-dark h-12"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#0A0A0F] border border-white/10">
                        <SelectItem value="5">5% — Light nudge</SelectItem>
                        <SelectItem value="10">10% — Real consequence</SelectItem>
                        <SelectItem value="15">15% — Serious commitment</SelectItem>
                        <SelectItem value="20">20% — Maximum discipline</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl">
                    <p className="text-sm text-[#A19DB0]">If you withdraw early, you'll lose <span className="text-amber-400 font-bold">{penaltyPercent}%</span> of your savings. This money is gone. Choose wisely.</p>
                  </div>
                </div>
              )}
              {vaultType === 'no_escape' && (
                <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <ShieldAlert className="h-5 w-5 text-red-400" />
                    <span className="text-white font-semibold">No Escape Mode</span>
                  </div>
                  <p className="text-sm text-[#A19DB0]">There is <span className="text-red-400 font-bold">absolutely no way</span> to withdraw early. Not even with a penalty. Your money is locked until {unlockDate ? format(unlockDate, "MMMM d, yyyy") : "your set date"}. Are you sure?</p>
                </div>
              )}
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-[#A19DB0] mb-2 block">Cooldown period before withdrawal</Label>
                <Select value={cooldownHours} onValueChange={setCooldownHours}>
                  <SelectTrigger className="input-dark h-12"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0A0A0F] border border-white/10">
                    <SelectItem value="24">24 hours — Think it over</SelectItem>
                    <SelectItem value="48">48 hours — Sleep on it twice</SelectItem>
                    <SelectItem value="72">72 hours — Maximum impulse protection</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-[#A19DB0] mt-1">Even after unlock, you'll wait this long before you can withdraw. Stops impulse spending.</p>
              </div>
              <div className="flex gap-3">
                <Button data-testid="create-step4-back-btn" onClick={() => setStep(3)} variant="outline" className="flex-1 btn-secondary">Back</Button>
                <Button data-testid="submit-goal-button" onClick={handleSubmit} disabled={loading} className="flex-1 h-12 btn-primary font-semibold">
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Lock It In'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function VaultTypeOption({ type, selected, onSelect, label, desc, free, premium }) {
  const isSelected = selected === type;
  return (
    <button
      onClick={() => onSelect(type)}
      className={`w-full text-left p-3 rounded-xl border transition-all ${
        isSelected 
          ? 'border-[#9333EA] bg-[#9333EA]/10' 
          : 'border-white/10 bg-[#0A0A0F] hover:bg-[#12121A]'
      }`}
    >
      <div className="flex items-center justify-between">
        <span className={`text-sm font-semibold ${isSelected ? 'text-white' : 'text-[#A19DB0]'}`}>{label}</span>
        {free && <span className="text-xs text-emerald-400 font-bold">FREE</span>}
        {premium && <span className="text-xs text-amber-400 font-bold flex items-center gap-1"><Crown className="h-3 w-3" /> PRO</span>}
      </div>
      <p className="text-xs text-[#A19DB0] mt-1">{desc}</p>
    </button>
  );
}

// ===================== DEPOSIT =====================
export function DepositModal({ open, onClose, goal, onSuccess, connectStatus }) {
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);

  const handlePay = async () => {
    if (!amount || parseFloat(amount) <= 0) { toast.error('Enter an amount to deposit'); return; }
    setLoading(true);
    try {
      const endpoint = connectStatus?.connected ? `${API}/api/connect/deposit` : `${API}/api/payments/create-checkout`;
      const res = await axios.post(endpoint, { goal_id: goal.goal_id, amount: parseFloat(amount), origin_url: window.location.origin }, { withCredentials: true });
      window.location.href = res.data.url;
    } catch (err) { 
      toast.error(err.response?.data?.detail || 'Payment failed. Try again.');
      setLoading(false); 
    }
  };

  const fee = amount && parseFloat(amount) > 0 ? (parseFloat(amount) * 0.02) : 0;
  const total = amount && parseFloat(amount) > 0 ? (parseFloat(amount) + fee) : 0;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="glass-modal sm:max-w-md" data-testid="deposit-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-white">Add to Vault</DialogTitle>
          <DialogDescription className="text-[#A19DB0]">
            Deposit into "{goal?.name}". This money will be locked until {new Date(goal?.unlock_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-4">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-[#A19DB0] mb-2 block">How much?</Label>
            <div className="relative">
              <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#A19DB0]" />
              <Input 
                data-testid="deposit-amount-input" 
                type="number" min="1" step="0.01" 
                value={amount} 
                onChange={(e) => setAmount(e.target.value)} 
                className="pl-12 input-dark h-14 text-lg font-mono" 
                placeholder="0.00"
                autoFocus
              />
            </div>
          </div>

          {total > 0 && (
            <div className="p-4 bg-[#12121A] rounded-xl border border-white/10 space-y-2">
              <div className="flex justify-between text-sm text-[#A19DB0]">
                <span>Your deposit</span>
                <span className="font-mono">${parseFloat(amount).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm text-[#A19DB0]">
                <span>Service fee (2%)</span>
                <span className="font-mono">${fee.toFixed(2)}</span>
              </div>
              <div className="border-t border-white/10 pt-2 flex justify-between text-base font-bold text-white">
                <span>You'll be charged</span>
                <span className="font-mono">${total.toFixed(2)}</span>
              </div>
            </div>
          )}

          <Button 
            data-testid="submit-stripe-deposit-button" 
            disabled={loading || !amount || parseFloat(amount) <= 0} 
            onClick={handlePay} 
            className="w-full h-14 btn-primary font-semibold text-base rounded-xl"
          >
            {loading ? (
              <><Loader2 className="h-5 w-5 animate-spin mr-2" /> Processing...</>
            ) : (
              <><CreditCard className="h-5 w-5 mr-2" /> Pay with Card</>
            )}
          </Button>

          <p className="text-center text-xs text-[#A19DB0]">
            Secure checkout powered by Stripe. Your card details are never stored by FutureVault.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ===================== VAULT DETAIL =====================
export function VaultDetailModal({ open, onClose, goal, onUpdate, connectStatus, onBankWithdraw }) {
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(goal?.name || '');
  const [targetAmount, setTargetAmount] = useState(goal?.target_amount?.toString() || '');
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [showRecurring, setShowRecurring] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [trackedRefunds, setTrackedRefunds] = useState(null);

  const isUnlocked = goal && new Date(goal.unlock_date) <= new Date();
  const progress = goal?.target_amount > 0 ? Math.min((goal.current_amount / goal.target_amount) * 100, 100) : 0;
  const hasRealDeposits = goal?.deposits?.some(d => ['stripe', 'connect', 'connect_deposit'].includes(d.type));
  const canDelete = isUnlocked || goal?.current_amount === 0 || !hasRealDeposits;
  const hasRefunds = goal?.deposits?.some(d => d.type === 'refund' || d.status === 'refunded');
  const refundTotal = goal?.deposits
    ?.filter(d => d.type === 'refund')
    ?.reduce((sum, d) => sum + Math.abs(d.amount || 0), 0) || 0;

  const handleUpdate = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/api/goals/${goal.goal_id}`, { name, target_amount: parseFloat(targetAmount) }, { withCredentials: true });
      toast.success('Vault updated!'); onUpdate(); setIsEditing(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Update failed'); }
    finally { setLoading(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this vault? This can\'t be undone.')) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/api/goals/${goal.goal_id}`, { withCredentials: true });
      toast.success('Vault deleted. You can create a new one anytime.');
      onUpdate(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Can\'t delete this vault right now'); }
    finally { setDeleting(false); }
  };

  const handleShare = async () => {
    try {
      const res = await axios.post(`${API}/api/goals/${goal.goal_id}/share`, {}, { withCredentials: true });
      const link = `${window.location.origin}/shared/${res.data.share_token}`;
      setShareLink(link);
      toast.success('Share link created!');
    } catch { toast.error('Couldn\'t create share link'); }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(shareLink);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  if (!goal) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="glass-modal sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="goal-details-modal">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${isUnlocked ? 'bg-emerald-500/20' : 'bg-[#9333EA]/20'}`}>
              {isUnlocked ? <Unlock className="h-6 w-6 text-emerald-400" /> : <Lock className="h-6 w-6 text-[#9333EA]" />}
            </div>
            <div>
              {isEditing ? (
                <Input data-testid="edit-goal-name-input" value={name} onChange={(e) => setName(e.target.value)} className="input-dark font-heading text-lg font-bold" />
              ) : (
                <h2 className="font-heading text-lg font-bold text-white">{goal.name}</h2>
              )}
              <span className={`text-xs font-semibold ${isUnlocked ? 'text-emerald-400' : 'text-[#9333EA]'}`}>
                {isUnlocked ? 'Unlocked — ready to withdraw' : `Locked until ${new Date(goal.unlock_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
              </span>
            </div>
          </div>
          <div className="flex gap-1">
            <Button variant="ghost" size="icon" data-testid="share-goal-button" onClick={handleShare} className="h-8 w-8 text-[#A19DB0] hover:text-white hover:bg-white/5">
              <Share2 className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" data-testid="edit-goal-button" onClick={() => setIsEditing(!isEditing)} className="h-8 w-8 text-[#A19DB0] hover:text-white hover:bg-white/5">
              <Edit className="h-4 w-4" />
            </Button>
            {canDelete && (
              <Button variant="ghost" size="icon" data-testid="delete-goal-button" onClick={handleDelete} disabled={deleting} className="h-8 w-8 text-red-400 hover:text-red-300 hover:bg-red-500/10">
                {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              </Button>
            )}
          </div>
        </div>

        <div className="mt-5 space-y-4">
          {/* Share Link */}
          {shareLink && (
            <div className="p-3 bg-[#9333EA]/10 border border-[#9333EA]/20 rounded-xl">
              <p className="text-xs text-[#9333EA] font-semibold mb-2">Share this link with friends or family who want to contribute</p>
              <div className="flex gap-2">
                <Input data-testid="share-link-input" value={shareLink} readOnly className="input-dark text-xs flex-1" />
                <Button data-testid="copy-share-link-button" size="sm" onClick={copyLink} className="btn-primary shrink-0">
                  {copied ? <CheckCircle className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          )}

          <VaultRefundTracker goalId={goal.goal_id} onLoaded={setTrackedRefunds} />

          <VaultSocialPanel goal={goal} onUpdate={onUpdate} />

          {/* Refund alert banner (only for refunds not issued through the tracker, e.g. issuer reversals) */}
          {hasRefunds && trackedRefunds === 0 && (
            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-start gap-3" data-testid="vault-refund-banner">
              <AlertTriangle className="h-5 w-5 text-red-400 mt-0.5 shrink-0" />
              <div className="text-sm">
                <p className="text-red-300 font-semibold">Stripe refunded {refundTotal > 0 ? `$${refundTotal.toFixed(2)}` : 'a deposit'} on this vault</p>
                <p className="text-[#A19DB0] text-xs mt-1">
                  Your card issuer (or Stripe's fraud system) reversed a recent deposit, so the balance here has been adjusted. If you didn't request this, check your Stripe Dashboard for details, or try depositing again with a different card.
                </p>
              </div>
            </div>
          )}

          {/* Amount & Progress */}
          <div className="p-5 bg-[#12121A] rounded-xl">
            <div className="flex justify-between items-baseline mb-3">
              <span className="font-mono text-3xl font-bold text-white">${goal.current_amount.toFixed(2)}</span>
              {isEditing ? (
                <div className="flex items-center gap-1">
                  <span className="text-[#A19DB0] text-sm">of $</span>
                  <Input data-testid="edit-target-amount-input" type="number" value={targetAmount} onChange={(e) => setTargetAmount(e.target.value)} className="input-dark w-20 text-right text-sm" />
                </div>
              ) : (
                <span className="text-[#A19DB0] text-sm">of ${goal.target_amount.toFixed(2)}</span>
              )}
            </div>
            <div className="w-full h-3 bg-[#0A0A0F] rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all duration-500 ${isUnlocked ? 'bg-emerald-400' : 'bg-[#9333EA]'}`} style={{ width: `${progress}%` }} />
            </div>
            <p className="text-right text-xs text-[#A19DB0] mt-1">{progress.toFixed(0)}% of target</p>
          </div>

          {/* Locked Message */}
          {!isUnlocked && goal.current_amount > 0 && (
            <div className="p-4 bg-[#9333EA]/5 border border-[#9333EA]/20 rounded-xl flex items-start gap-3">
              <Lock className="h-5 w-5 text-[#9333EA] mt-0.5 shrink-0" />
              <p className="text-sm text-[#A19DB0]">
                This money is locked until <span className="text-white font-semibold">{new Date(goal.unlock_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>. 
                You won't be able to withdraw before this date. Stay strong!              </p>
            </div>
          )}

          {/* Bank Withdraw Button */}
          {isUnlocked && connectStatus?.connected && goal.current_amount > 0 && (
            <Button
              data-testid="withdraw-to-bank-button"
              onClick={onBankWithdraw}
              className="w-full h-14 bg-emerald-500 hover:bg-emerald-400 text-white font-semibold text-base rounded-xl transition-colors"
            >
              <ArrowDownToLine className="h-5 w-5 mr-2" />
              Withdraw ${goal.current_amount.toFixed(2)} to Bank
            </Button>
          )}

          {/* Unlocked warning */}
          {isUnlocked && goal.current_amount > 0 && !connectStatus?.connected && (
            <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm text-white font-semibold">Connect your bank to withdraw</p>
                <p className="text-xs text-[#A19DB0] mt-1">Tap "Connect Bank" in the header to link your bank account and withdraw your funds.</p>
              </div>
            </div>
          )}

          {/* Recurring Setup */}
          <Button data-testid="recurring-deposit-button" onClick={() => setShowRecurring(!showRecurring)} variant="outline" className="w-full btn-secondary rounded-xl">
            <Repeat className="h-4 w-4 mr-2" /> {showRecurring ? 'Hide' : 'Set Up'} Auto-Save
          </Button>
          {showRecurring && <RecurringSection goalId={goal.goal_id} />}

          {/* Recent Activity */}
          {goal.deposits && goal.deposits.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-[#A19DB0] mb-2">Recent Activity</h4>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {goal.deposits.slice(-5).reverse().map((dep, i) => {
                  const isRefund = dep.type === 'refund' || dep.status === 'refunded';
                  const isWithdrawal = dep.type === 'withdrawal' || dep.type === 'bank_withdrawal';
                  const iconBg = isRefund ? 'bg-red-500/20' : isWithdrawal ? 'bg-red-500/20' : dep.type === 'recurring' ? 'bg-amber-500/20' : 'bg-[#9333EA]/20';
                  const icon = isRefund ? <ArrowDownToLine className="h-3.5 w-3.5 text-red-400" /> :
                               isWithdrawal ? <ArrowDownToLine className="h-3.5 w-3.5 text-red-400" /> :
                               dep.type === 'recurring' ? <Repeat className="h-3.5 w-3.5 text-amber-400" /> :
                               <DollarSign className="h-3.5 w-3.5 text-[#9333EA]" />;
                  return (
                    <div key={dep.deposit_id || i} className="flex items-center justify-between p-3 bg-[#0A0A0F] rounded-lg border border-white/5" data-testid={`deposit-row-${i}`}>
                      <div className="flex items-center gap-2">
                        <div className={`h-7 w-7 rounded-full flex items-center justify-center ${iconBg}`}>
                          {icon}
                        </div>
                        <div className="flex flex-col">
                          <span className="text-xs text-[#A19DB0] capitalize">
                            {dep.type === 'bank_withdrawal' ? 'Bank withdrawal' : dep.type === 'refund' ? 'Refunded by Stripe' : dep.type}
                          </span>
                          {dep.status === 'refunded' && dep.type !== 'refund' && (
                            <span className="text-[9px] font-bold uppercase tracking-wider text-red-400" data-testid="deposit-refunded-badge">Refunded</span>
                          )}
                        </div>
                      </div>
                      <span className={`font-mono text-sm font-semibold ${dep.amount < 0 || dep.status === 'refunded' ? 'text-red-400 line-through opacity-70' : 'text-emerald-400'}`}>
                        {dep.amount < 0 ? '-' : '+'}${Math.abs(dep.amount).toFixed(2)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Save edits */}
          {isEditing && (
            <Button data-testid="save-goal-changes-button" onClick={handleUpdate} disabled={loading} className="w-full h-12 btn-primary rounded-xl">
              {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Saving...</> : 'Save Changes'}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ===================== RECURRING SECTION =====================
function RecurringSection({ goalId }) {
  const [amount, setAmount] = useState('');
  const [frequency, setFrequency] = useState('weekly');
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [fetched, setFetched] = useState(false);

  React.useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/api/goals/${goalId}/recurring`, { withCredentials: true });
        if (res.data?.recurring_id) { setConfig(res.data); setAmount(res.data.amount?.toString() || ''); setFrequency(res.data.frequency || 'weekly'); }
      } catch {} finally { setFetched(true); }
    };
    fetch();
  }, [goalId]);

  const handleSave = async () => {
    if (!amount || parseFloat(amount) <= 0) { toast.error('Enter an amount'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/api/goals/${goalId}/recurring`, { goal_id: goalId, amount: parseFloat(amount), frequency, enabled: true }, { withCredentials: true });
      toast.success(`Auto-save set! $${parseFloat(amount).toFixed(2)} ${frequency}.`);
      setConfig({ amount: parseFloat(amount), frequency, enabled: true });
    } catch { toast.error('Couldn\'t set up auto-save'); }
    finally { setLoading(false); }
  };

  const handleRemove = async () => {
    try {
      await axios.delete(`${API}/api/goals/${goalId}/recurring`, { withCredentials: true });
      toast.success('Auto-save removed'); setConfig(null); setAmount(''); setFrequency('weekly');
    } catch { toast.error('Couldn\'t remove auto-save'); }
  };

  if (!fetched) return null;

  return (
    <div className="p-4 bg-[#12121A] rounded-xl border border-white/10 space-y-3" data-testid="recurring-deposit-section">
      <p className="text-xs text-[#A19DB0]">Automatically add to this vault on a schedule. Set it and forget it.</p>
      {config ? (
        <div className="space-y-3">
          <div className="p-3 bg-[#9333EA]/10 rounded-lg">
            <p className="text-sm text-white"><span className="font-mono font-bold">${config.amount?.toFixed(2)}</span> every <span className="font-semibold capitalize">{config.frequency}</span></p>
          </div>
          <Button data-testid="remove-recurring-button" variant="outline" size="sm" onClick={handleRemove} className="w-full text-red-400 border-red-500/20 hover:bg-red-500/10 rounded-lg">
            Remove Auto-Save
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-[#A19DB0]">Amount</Label>
              <div className="relative mt-1">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-3 w-3 text-[#A19DB0]" />
                <Input data-testid="recurring-amount-input" type="number" min="1" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="pl-8 input-dark text-sm rounded-lg" placeholder="25.00" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-[#A19DB0]">Frequency</Label>
              <Select value={frequency} onValueChange={setFrequency}>
                <SelectTrigger data-testid="recurring-frequency-select" className="mt-1 input-dark text-sm rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0A0A0F] border border-white/10">
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="biweekly">Every 2 weeks</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button data-testid="save-recurring-button" size="sm" onClick={handleSave} disabled={loading} className="w-full btn-primary rounded-lg">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Repeat className="h-4 w-4 mr-2" /> Start Auto-Save</>}
          </Button>
        </div>
      )}
    </div>
  );
}
